"use client";

import React from "react";
import { SlidersHorizontal, ShieldCheck, ArrowRight, FileText, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
const copy = {
  categoryBadge: {
    en: "Filter Components Engineering",
    zh: "过滤组件工程规范",
    es: "Ingeniería de componentes filtrantes",
    de: "Filterkomponenten-Engineering",
    fr: "Ingénierie des composants de filtration",
    pt: "Engenharia de componentes filtrantes"
  },
  h1: {
    en: "Filter Components Selection Guide",
    zh: "过滤组件选型指南",
    es: "Guía de selección de componentes filtrantes",
    de: "Auswahlleitfaden für Filterkomponenten",
    fr: "Guide de sélection des composants de filtration",
    pt: "Guia de seleção de componentes de filtração"
  },
  lead: {
    en: "Technical selection criteria for stainless steel filter discs, cylindrical screen packs, and fabricated mesh components. Evaluate micron retention, differential pressure, edge sealing, and alloy specifications for reliable process filtration.",
    zh: "不锈钢冲压滤圆片、圆筒滤网、组合滤网及深加工组件的技术选型指南。系统评估过滤精度、压降耐受、边缘密封结构与合金牌号，确保工业工况下稳定可靠的过滤效能。",
    es: "Criterios técnicos de selección para discos filtrantes de acero inoxidable, paquetes de malla cilíndricos y componentes manufacturados. Evalúe micraje, presión diferencial, sellado de bordes y aleaciones para una filtración de procesos confiable.",
    de: "Technische Auswahlkriterien für Edelstahl-Filterronden, zylindrische Siebpakete und konfektionierte Drahtgewebeteile. Bewerten Sie Filterfeinheit, Differenzdruck, Randabdichtung und Legierungen für eine zuverlässige Prozessfiltration.",
    fr: "Critères de sélection technique pour disques filtrants en acier inoxydable, packs tamis cylindriques et composants façonnés. Évaluez le seuil de filtration, la perte de charge, l'étanchéité des bords et les alliages pour une filtration industrielle fiable.",
    pt: "Critérios de seleção técnica para discos filtrantes de aço inoxidável, pacotes cilíndricos e componentes usinados. Avalie micragem, pressão diferencial, vedação perimetral e ligas para filtração confiável em processos industriais."
  },
  secondaryBtn: {
    en: "Selection Criteria",
    zh: "核心选型标准",
    es: "Criterios de selección",
    de: "Auswahlkriterien",
    fr: "Critères de sélection",
    pt: "Critérios de seleção"
  },
  overlayChip: {
    en: "ISO 9044 / ASTM E2016 Compliant",
    zh: "符合 ISO 9044 / ASTM E2016",
    es: "Conforme a ISO 9044 / ASTM E2016",
    de: "Konform nach ISO 9044 / ASTM E2016",
    fr: "Conforme ISO 9044 / ASTM E2016",
    pt: "Conforme ISO 9044 / ASTM E2016"
  },
  cardNote: {
    en: "Discs, cylinders, packs & pleated components",
    zh: "滤圆片、圆筒滤筒、组合网片及折叠滤芯",
    es: "Discos, cilindros, paquetes y elementos plisados",
    de: "Ronden, Zylinder, Pakete & Plisseeelemente",
    fr: "Disques, cylindres, packs & éléments plissés",
    pt: "Discos, cilindros, pacotes e elementos plissados"
  },
  cardLink: {
    en: "All components",
    zh: "所有组件",
    es: "Todos los componentes",
    de: "Alle Komponenten",
    fr: "Tous les composants",
    pt: "Todos os componentes"
  },
  specRetentionLabel: {
    en: "Retention Range",
    zh: "过滤精度范围",
    es: "Rango de retención",
    de: "Rückhaltebereich",
    fr: "Plage de rétention",
    pt: "Faixa de retenção"
  },
  specRetentionVal: {
    en: "1 µm – 25 mm",
    zh: "1 µm – 25 mm",
    es: "1 µm – 25 mm",
    de: "1 µm – 25 mm",
    fr: "1 µm – 25 mm",
    pt: "1 µm – 25 mm"
  },
  specMeshLabel: {
    en: "Mesh Specification",
    zh: "丝网目数范围",
    es: "Rango de mallas",
    de: "Maschenbereich",
    fr: "Gamme de mailles",
    pt: "Faixa de malhas"
  },
  specMeshVal: {
    en: "1 – 635 Mesh",
    zh: "1 – 635 目",
    es: "1 – 635 mallas",
    de: "1 – 635 Mesh",
    fr: "1 – 635 mailles",
    pt: "1 – 635 malhas"
  },
  specAlloysLabel: {
    en: "Standard Alloys",
    zh: "常用合金牌号",
    es: "Aleaciones estándar",
    de: "Standardlegierungen",
    fr: "Alliages standard",
    pt: "Ligas padrão"
  },
  specAlloysVal: {
    en: "AISI 304 / 304L / 316 / 316L",
    zh: "AISI 304 / 304L / 316 / 316L",
    es: "AISI 304 / 304L / 316 / 316L",
    de: "AISI 304 / 304L / 316 / 316L",
    fr: "AISI 304 / 304L / 316 / 316L",
    pt: "AISI 304 / 304L / 316 / 316L"
  },
  specStandardsLabel: {
    en: "Manufacturing Standards",
    zh: "制造执行标准",
    es: "Normas de fabricación",
    de: "Fertigungsnormen",
    fr: "Normes de fabrication",
    pt: "Normas de fabricação"
  },
  specStandardsVal: {
    en: "ISO 9044 / ASTM E2016",
    zh: "ISO 9044 / ASTM E2016",
    es: "ISO 9044 / ASTM E2016",
    de: "ISO 9044 / ASTM E2016",
    fr: "ISO 9044 / ASTM E2016",
    pt: "ISO 9044 / ASTM E2016"
  }
};
export default function ComponentsSelectionHero() {
  const {
    lang
  } = useLanguage();
  const technicalHighlights = [{
    label: pick(lang, copy.specRetentionLabel),
    val: pick(lang, copy.specRetentionVal)
  }, {
    label: pick(lang, copy.specMeshLabel),
    val: pick(lang, copy.specMeshVal)
  }, {
    label: pick(lang, copy.specAlloysLabel),
    val: pick(lang, copy.specAlloysVal)
  }, {
    label: pick(lang, copy.specStandardsLabel),
    val: pick(lang, copy.specStandardsVal)
  }];
  return <section id="herofiltercomponents" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Technical Badges Row: Exactly two badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{pick(lang, copy.categoryBadge)}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>ISO 9044 / ASTM E2016</span>
              </Badge>
            </div>

            {/* Single Page Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {pick(lang, copy.h1)}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {pick(lang, copy.lead)}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-6 font-semibold">
                <Link to="/FilterComponentsPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{pick(lang, copy.secondaryBtn)}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Card (5 Columns on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="mf-card-media mf-ratio-hero w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-custom-oem-st-585e14.png" alt={pick(lang, {
                en: "Stainless steel filter components, custom discs, screen packs, and fabricated cylinders",
                zh: "不锈钢过滤组件、定制冲压滤圆片、组合滤网及圆筒深加工件",
                es: "Componentes de filtro de acero inoxidable, discos, paquetes de malla y cilindros",
                de: "Edelstahl-Filterkomponenten, Ronden, Siebpakete und gefertigte Zylinder",
                fr: "Composants filtrants en acier inoxydable, disques, packs et cylindres façonnés",
                pt: "Componentes de filtro em aço inoxidável, discos, pacotes de tela e cilindros usinados"
              })} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{pick(lang, copy.overlayChip)}</span>
                </div>
              </div>

              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{pick(lang, copy.cardNote)}</span>
                  </div>
                  <Link to="/FilterComponentsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{pick(lang, copy.cardLink)}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip directly below grid inside the same container */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.val}</span>
            </div>)}
        </div>
      </div>
    </section>;
}